import { Box, Button, CircularProgress, Link, Stack, TextField, Typography } from '@mui/material';
import axios from 'axios';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { LoginApi } from '../api/LoginApi';
import VerbaScholaAccountCard, { accountBodySx, accountLinkSx } from '../component/VerbaScholaAccountCard';
import { primaryButtonSx } from '../style/verbaScholaPublicStyles';

// Forgot password. The API answers the same way whether or not the address has an account,
// so this page never reveals which addresses are registered.
export default function ResetPasswordEmail() {
    const [email, setEmail] = React.useState('');
    const [sentTo, setSentTo] = React.useState<string | null>(null);
    const [isSending, setIsSending] = React.useState(false);
    const [failure, setFailure] = React.useState<'none' | 'limited' | 'unavailable'>('none');

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isSending) return;
        setIsSending(true);
        setFailure('none');
        LoginApi.sendResetPasswordEmail(email.trim())
            .then(() => setSentTo(email.trim()))
            .catch(err => setFailure(axios.isAxiosError(err) && err.response?.status === 429 ? 'limited' : 'unavailable'))
            .finally(() => setIsSending(false));
    };

    if (sentTo !== null) {
        return (
            <VerbaScholaAccountCard title="Check your email">
                <Typography sx={{ ...accountBodySx, mt: 2 }} role="status">
                    If an account uses <Box component="strong" sx={{ wordBreak: 'break-all' }}>{sentTo}</Box>, look for an email
                    with a link to choose a new password. The link works once and expires in 1 hour.
                </Typography>
                <Typography sx={{ ...accountBodySx, mt: 1.5 }}>Check your spam folder if it doesn&apos;t arrive.</Typography>
                <Stack spacing={1} sx={{ mt: 3 }}>
                    <Button component={RouterLink} to="/login" sx={{ ...primaryButtonSx, minHeight: 48 }}>Back to log in</Button>
                    <Button variant="text" onClick={() => setSentTo(null)}>Use a different email</Button>
                </Stack>
            </VerbaScholaAccountCard>
        );
    }

    return (
        <VerbaScholaAccountCard title="Forgot your password?" intro="Enter the email address you use for VerbaSchola and we'll send you a link to choose a new password.">
            {failure !== 'none' && (
                <Typography role="alert" sx={{ ...accountBodySx, mt: 2, color: 'error.dark' }}>
                    {failure === 'limited'
                        ? 'Too many reset requests from this network. Wait a while, then try again.'
                        : 'We couldn’t send the email right now. Check your connection and try again.'}
                </Typography>
            )}
            <Stack component="form" spacing={2} onSubmit={handleSubmit} sx={{ mt: 3 }}>
                <TextField id="email" label="Email address" name="email" type="email" autoComplete="email" autoFocus required fullWidth
                    value={email} onChange={event => setEmail(event.target.value)} />
                <Button type="submit" disabled={isSending} sx={{ ...primaryButtonSx, minHeight: 48 }}>
                    {isSending
                        ? <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} sx={{ color: 'inherit' }} aria-hidden /><span>Sending…</span></Stack>
                        : 'Send reset link'}
                </Button>
            </Stack>
            <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Link component={RouterLink} to="/login" underline="hover" sx={accountLinkSx}>Back to log in</Link>
            </Box>
        </VerbaScholaAccountCard>
    );
}
